import { memo, ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';

import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import Event, {
  EVENT_MEMBERGUEST_TYPES,
  EVENT_TEAM_DEFINED_BY_TYPES,
  EventMemberGuestType,
  EventPartnership,
  EventTeamStructure,
  PARTNERSHIP_DEFINED_BY_TYPES,
  PartnershipDefinedByType,
} from '../../types/Event';
import { getPartnershipDefinedBy, isAdHocPartnershipEvent, isPartnershipEvent as computeIsPartnershipEvent } from '../../utils/eventTeamUtils';

import { FieldTarget } from './eventPreviewTypes';

type EventMembershipTypeEditorProps = {
  event: Event;
  isGolf: boolean;
  isTeamLeague?: boolean;
  defaultTeamSize?: number;
  onUpdate: (updates: Partial<Event>) => void;
  /** Field to focus when the editor expands */
  focusTarget?: FieldTarget | null;
  /** Called after focus has been applied */
  onFocusComplete?: () => void;
}

// Dual-written during the teamStructure -> partnership migration (see EventPartnership's own
// deprecation note) — every write sends both shapes so nothing reading the deprecated field breaks
// mid-migration. Kept as one conversion so the two shapes can't drift apart from each other.
function toLegacyTeamStructure(partnership: EventPartnership): EventTeamStructure {
  return {
    teamDefinedBy: partnership.definedBy as unknown as EventTeamStructure['teamDefinedBy'],
    memberGuest: partnership.memberGuest,
    size: partnership.size,
    maxGuests: partnership.maxGuests,
  };
}

const EventMembershipTypeEditor = memo(function EventMembershipTypeEditor({
  event,
  isGolf,
  isTeamLeague = false,
  defaultTeamSize,
  onUpdate,
  focusTarget,
  onFocusComplete,
}: EventMembershipTypeEditorProps): ReactElement {
  const teamSizeRef = useRef<HTMLDivElement>(null);

  // Handle focus target from parent (e.g. "Change" on the "X per team" preview line)
  useEffect(() => {
    if (focusTarget === 'teamStructure' && teamSizeRef.current) {
      const combobox = teamSizeRef.current.querySelector('[role="combobox"]') as HTMLElement | null;
      combobox?.focus();
      onFocusComplete?.();
    }
  }, [focusTarget, onFocusComplete]);

  const isEventPartnership = computeIsPartnershipEvent(event);
  const isMemberOnly = event.isMemberOnly ?? false;
  const partnership = useMemo<EventPartnership | undefined>(() => event.partnership ?? (event.teamStructure ? {
    definedBy: event.teamStructure.teamDefinedBy as unknown as PartnershipDefinedByType | undefined,
    memberGuest: event.teamStructure.memberGuest,
    size: event.teamStructure.size,
    maxGuests: event.teamStructure.maxGuests,
  } : undefined), [event.partnership, event.teamStructure]);
  const partnershipDefinedBy = getPartnershipDefinedBy(event);

  const getTopToggleValue = (): 'individual' | 'partnership' | 'team' => {
    if (!isEventPartnership) return 'individual';
    if (partnershipDefinedBy === PARTNERSHIP_DEFINED_BY_TYPES.LEAGUE) return 'team';
    return 'partnership';
  };

  const handleTopToggleChange = useCallback((_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue === null) return;

    if (newValue === 'partnership') {
      const defaultPartnership: EventPartnership = {
        definedBy: PARTNERSHIP_DEFINED_BY_TYPES.PLAYER,
        memberGuest: EVENT_MEMBERGUEST_TYPES.MEMBER_ONLY,
        size: 2,
      };
      onUpdate({ isPartnershipEvent: true, isTeamEvent: true, isMemberOnly: false, partnership: defaultPartnership, teamStructure: toLegacyTeamStructure(defaultPartnership) });
    } else if (newValue === 'team') {
      const defaultPartnership: EventPartnership = {
        definedBy: PARTNERSHIP_DEFINED_BY_TYPES.LEAGUE,
        memberGuest: EVENT_MEMBERGUEST_TYPES.MEMBER_ONLY,
        size: defaultTeamSize ?? 4,
      };
      onUpdate({ isPartnershipEvent: true, isTeamEvent: true, isMemberOnly: false, partnership: defaultPartnership, teamStructure: toLegacyTeamStructure(defaultPartnership) });
    } else {
      onUpdate({ isPartnershipEvent: false, isTeamEvent: false, isMemberOnly: false, partnership: undefined, teamStructure: undefined });
    }
  }, [defaultTeamSize, onUpdate]);

  const handleMemberOnlyChange = useCallback((_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue === null) return;
    onUpdate({ isMemberOnly: newValue === 'membersOnly' });
  }, [onUpdate]);

  // Who defines partnerships sub-selector (PLAYER vs ADMIN) — only for partnership events
  const handlePartnershipDefinedByChange = useCallback((_: React.MouseEvent<HTMLElement>, newValue: PartnershipDefinedByType | null) => {
    if (newValue === null || !partnership) return;
    const updated: EventPartnership = {
      ...partnership,
      definedBy: newValue,
      // PLAYER partnerships are always size 2; ADMIN can be any size
      size: newValue === PARTNERSHIP_DEFINED_BY_TYPES.PLAYER ? 2 : (partnership.size > 2 ? partnership.size : (defaultTeamSize ?? 4)),
    };
    onUpdate({ partnership: updated, teamStructure: toLegacyTeamStructure(updated) });
  }, [partnership, defaultTeamSize, onUpdate]);

  const handleTeamSizeChange = useCallback((e: SelectChangeEvent<number>) => {
    if (!partnership) return;
    const newSize = Number(e.target.value);
    const updated: EventPartnership = {
      ...partnership,
      size: newSize,
      maxGuests: partnership.maxGuests ? Math.min(partnership.maxGuests, newSize - 1) : partnership.maxGuests,
    };
    onUpdate({ partnership: updated, teamStructure: toLegacyTeamStructure(updated) });
  }, [partnership, onUpdate]);

  const handleTeamCompositionChange = useCallback((_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue === null || !partnership) return;
    const memberGuest = newValue as EventMemberGuestType;
    const updated: EventPartnership = {
      ...partnership,
      memberGuest,
      maxGuests: memberGuest === EVENT_MEMBERGUEST_TYPES.MEMBER_GUEST ? 1 : undefined,
    };
    onUpdate({ partnership: updated, teamStructure: toLegacyTeamStructure(updated) });
  }, [partnership, onUpdate]);

  const getPartnershipCompositionValue = (): 'memberMember' | 'memberGuest' | 'open' => {
    if (!partnership) return 'memberMember';
    if (partnership.memberGuest === EVENT_MEMBERGUEST_TYPES.MEMBER_ONLY) return 'memberMember';
    if (partnership.maxGuests === 2) return 'open';
    return 'memberGuest';
  };

  const handlePartnershipCompositionChange = useCallback((_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue === null || !partnership) return;
    let updated: EventPartnership;

    if (newValue === 'memberMember') {
      updated = { ...partnership, memberGuest: EVENT_MEMBERGUEST_TYPES.MEMBER_ONLY, maxGuests: undefined };
    } else if (newValue === 'memberGuest') {
      updated = { ...partnership, memberGuest: EVENT_MEMBERGUEST_TYPES.MEMBER_GUEST, maxGuests: 1 };
    } else {
      updated = { ...partnership, memberGuest: EVENT_MEMBERGUEST_TYPES.MEMBER_GUEST, maxGuests: 2 };
    }
    onUpdate({ partnership: updated, teamStructure: toLegacyTeamStructure(updated) });
  }, [partnership, onUpdate]);

  const handleMaxGuestsChange = useCallback((e: SelectChangeEvent<number>) => {
    if (!partnership) return;
    const updated: EventPartnership = { ...partnership, maxGuests: Number(e.target.value) };
    onUpdate({ partnership: updated, teamStructure: toLegacyTeamStructure(updated) });
  }, [partnership, onUpdate]);

  const isAdHocPartnership = isAdHocPartnershipEvent(event);
  const isPlayerPartnership = partnershipDefinedBy === PARTNERSHIP_DEFINED_BY_TYPES.PLAYER;
  const isAdminPartnership = partnershipDefinedBy === PARTNERSHIP_DEFINED_BY_TYPES.ADMIN;

  const maxGuestsOptions = partnership
    ? Array.from({ length: partnership.size - 1 }, (_, i) => i + 1)
    : [1];

  return (
    <Stack spacing={2}>
      {/* Top toggle: Individual / Partnership / Team */}
      <FormControl component="fieldset">
        <FormLabel component="legend" sx={{ mb: 0.5 }}>Event type</FormLabel>
        <ToggleButtonGroup
          aria-label="Event type"
          value={getTopToggleValue()}
          exclusive
          onChange={handleTopToggleChange}
          size="small"
        >
          <ToggleButton value="individual">Individual</ToggleButton>
          <ToggleButton value="partnership">Partnership</ToggleButton>
          {isTeamLeague && (
            <ToggleButton value="team">Team</ToggleButton>
          )}
        </ToggleButtonGroup>
      </FormControl>

      {/* Individual event options */}
      {!isEventPartnership && (
        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ mb: 0.5 }}>Participation</FormLabel>
          <ToggleButtonGroup
            aria-label="Participation restriction"
            value={isMemberOnly ? 'membersOnly' : 'openToAll'}
            exclusive
            onChange={handleMemberOnlyChange}
            size="small"
          >
            <ToggleButton value="openToAll">Open to all</ToggleButton>
            <ToggleButton value="membersOnly">Members only</ToggleButton>
          </ToggleButtonGroup>
        </FormControl>
      )}

      {/* Partnership options */}
      {isAdHocPartnership && partnership && (
        <>
          {/* Who defines partnerships: Player chooses vs Admin assigns */}
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 0.5 }}>Teams defined by</FormLabel>
            <ToggleButtonGroup
              aria-label="Who defines teams"
              value={partnershipDefinedBy ?? PARTNERSHIP_DEFINED_BY_TYPES.PLAYER}
              exclusive
              onChange={handlePartnershipDefinedByChange}
              size="small"
            >
              <ToggleButton value={EVENT_TEAM_DEFINED_BY_TYPES.PLAYER}>Player chooses</ToggleButton>
              <ToggleButton value={EVENT_TEAM_DEFINED_BY_TYPES.ADMIN}>Admin assigns</ToggleButton>
              <ToggleButton value={EVENT_TEAM_DEFINED_BY_TYPES.PAIRING}>Tee sheet</ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          {/* Team size — only for Admin (Player always 2; Pairing determined by tee sheet) */}
          {isAdminPartnership && (
            <FormControl size="small" sx={{ maxWidth: '10em' }} ref={teamSizeRef}>
              <FormLabel component="legend" sx={{ mb: 0.5 }}>Team size</FormLabel>
              <Select value={partnership.size} onChange={handleTeamSizeChange}>
                {Array.from({ length: 19 }, (_, i) => i + 2).map((size) => (
                  <MenuItem key={size} value={size}>{size} players</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Partnership composition (Member/Member, Member/Guest) — for Player partnerships (size 2) */}
          {isPlayerPartnership && (
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 0.5 }}>Partnership composition</FormLabel>
              <ToggleButtonGroup
                aria-label="Partnership composition"
                value={getPartnershipCompositionValue()}
                exclusive
                onChange={handlePartnershipCompositionChange}
                size="small"
              >
                <ToggleButton value="memberMember">Member / Member</ToggleButton>
                <ToggleButton value="memberGuest">Member / Guest</ToggleButton>
                <ToggleButton value="open">Open</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          )}

          {/* Team composition — for Admin partnerships */}
          {isAdminPartnership && (
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 0.5 }}>Team composition</FormLabel>
              <ToggleButtonGroup
                aria-label="Team composition"
                value={partnership.memberGuest}
                exclusive
                onChange={handleTeamCompositionChange}
                size="small"
              >
                <ToggleButton value={EVENT_MEMBERGUEST_TYPES.MEMBER_ONLY}>Members only</ToggleButton>
                <ToggleButton value={EVENT_MEMBERGUEST_TYPES.MEMBER_GUEST}>Open</ToggleButton>
                <ToggleButton value={EVENT_MEMBERGUEST_TYPES.GUEST_ONLY}>Guests only</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          )}

          {/* Max guests — for Admin partnerships with mixed composition */}
          {isAdminPartnership && partnership.memberGuest === EVENT_MEMBERGUEST_TYPES.MEMBER_GUEST && (
            <FormControl size="small" sx={{ maxWidth: '12em' }}>
              <FormLabel component="legend" sx={{ mb: 0.5 }}>Max guests per team</FormLabel>
              <Select value={partnership.maxGuests ?? 1} onChange={handleMaxGuestsChange}>
                {maxGuestsOptions.map((num) => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </>
      )}

      {/* League team options */}
      {isEventPartnership && partnershipDefinedBy === PARTNERSHIP_DEFINED_BY_TYPES.LEAGUE && partnership && (
        <>
          <FormControl size="small" sx={{ maxWidth: '10em' }} ref={teamSizeRef}>
            <FormLabel component="legend" sx={{ mb: 0.5 }}>Team size</FormLabel>
            <Select value={partnership.size} onChange={handleTeamSizeChange}>
              {Array.from({ length: 19 }, (_, i) => i + 2).map((size) => (
                <MenuItem key={size} value={size}>{size} players</MenuItem>
              ))}
            </Select>
          </FormControl>

          {isGolf && (
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 0.5 }}>Team composition</FormLabel>
              <ToggleButtonGroup
                aria-label="Team composition"
                value={partnership.memberGuest}
                exclusive
                onChange={handleTeamCompositionChange}
                size="small"
              >
                <ToggleButton value={EVENT_MEMBERGUEST_TYPES.MEMBER_ONLY}>Members only</ToggleButton>
                <ToggleButton value={EVENT_MEMBERGUEST_TYPES.MEMBER_GUEST}>Open</ToggleButton>
                <ToggleButton value={EVENT_MEMBERGUEST_TYPES.GUEST_ONLY}>Guests only</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          )}

          {isGolf && partnership.memberGuest === EVENT_MEMBERGUEST_TYPES.MEMBER_GUEST && (
            <FormControl size="small" sx={{ maxWidth: '12em' }}>
              <FormLabel component="legend" sx={{ mb: 0.5 }}>Max guests per team</FormLabel>
              <Select value={partnership.maxGuests ?? 1} onChange={handleMaxGuestsChange}>
                {maxGuestsOptions.map((num) => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </>
      )}
    </Stack>
  );
});

export default EventMembershipTypeEditor;
